<template>
  <scroll-container data-module="about"  @change="updateSectionScrollState">
    <ScrollItem :id="`topOfAboutContent`">
      <div data-anchor="topOfAboutContent"></div>
    </ScrollItem>
    <ScrollItem :id="`bottomOfAboutContent`">
      <div data-anchor="bottomOfAboutContent"></div>
    </ScrollItem>
    <div class="about__content" :data-visibility="graphicVisibility">
      <component_hcAboutRelevant 
        :scrollState="sectionScroll" 
        :visibility="graphicVisibility"
        :percentScrolled="percentScrolled"
      />
      <component_hcAboutMemorable 
        :scrollState="sectionScroll" 
        :visibility="graphicVisibility"
        :percentScrolled="percentScrolled"
      />
      <component_hcAboutAlive 
        :scrollState="sectionScroll" 
        :visibility="graphicVisibility"
        :percentScrolled="percentScrolled"
      />
    </div>
    <div
      class="about__nav"
      :class="aboutNavClasses"
      :style="aboutNavStyles"
      v-if="graphicVisibility !== 'mobile'"
    >
      <div class="nav__indicator" :style="aboutNavPositionStyle"></div>
      <div 
        class="nav__section-link" 
      >
        <a href="#hcAboutRelevant" class="section-link__copy">
          01 Relevant
        </a>
      </div>
      <div 
        class="nav__section-link" 
      >
        <a href="#hcAboutMemorable" class="section-link__copy">
          02 Memorable
        </a>
      </div>
      <div 
        class="nav__section-link"
      >
        <a href="#hcAboutAlive" class="section-link__copy">
          03 Alive
        </a>
      </div>
    </div>
  </scroll-container>
</template>


<script>
export default {
  name: 'module_hcAbout',
  props: ['window', 'scrollState', 'scrollPosition'],
  data: function() {
    return {
      container: null,
      sections: null,
      sectionScroll: {},
    };
  },
  computed: {
    percentScrolled: function() {
      if (!this.container) { return 0; }
      const vh = this.window.height;

      const containerOffset = this.container.offsetTop - this.window.height;
      const offset = Math.max(0, this.scrollPosition - containerOffset);

      const percent = (offset / vh) * 100;
      return percent;
    },
    graphicVisibility: function() {
      // set the breakpoints for graphic display
      const w = this.window.width;
      const h = this.window.height;

      const ratio = w / h;
      let display = 'mobile';

      if (ratio > 0.9) {
        display = 'compact';
      }

      if (ratio > 1.45) {
        display = 'full';
      }

      if (h > 1024) {
        display = 'compact';

        if (ratio < 0.8) {
          display = 'mobile';
        }
      }

      if (h > 1700) {
        display = 'mobile';
      }

      if (w < 768) {
        display = 'mobile';
      }

      return display;
    },
    aboutNavClasses: function() {
      if (!this.sectionScroll.bottomOfAboutContent || !this.sectionScroll.topOfAboutContent) { return {}; }
      return {
        following: this.sectionScroll.topOfAboutContent.isAboveViewport && this.sectionScroll.bottomOfAboutContent.isBelowViewport,
        'fixed--bottom': this.sectionScroll.bottomOfAboutContent.isInViewport,
      };
    },
    aboutNavStyles: function() {
      // since we're using position fixed, right-side
      // we'll need to manually calculate what that right value is
      try {
        if (this.sectionScroll.topOfAboutContent.isAboveViewport &&
            !this.sectionScroll.bottomOfAboutContent.isInViewport &&
            this.window.width > 1024) {
          const contentWidth = document.querySelector('[data-module="about"]').clientWidth;

          return {
            transform: 'translateX(-100%)',
            left: `calc(50% + ${contentWidth / 2}px)`,
          };
        }
      } catch (e) {
        return {};
      }

      return {};
    },
    aboutNavPositionStyle: function() {
      const position = this.percentScrolled;
      let track = 0;

      // irregular scrolling - manually scrub nav bar
      if (position < 100) {
        // percent of scroll (100) traveled to full opacity
        track = (position / 100) * 100;
      } else if (position < 125) {
        // end of full opacity
        track = 100;
      } else if (position < 225) {
        // percent of scroll (100) traveled to full opacity
        const dist = 100;
        const travel = position - 125; // 125 = full opacity start point
        const ratio = (travel / dist) * 100;
        track = 100 + ratio;
      } else if (position < 250) {
        track = 200;
      } else if (position < 350) {
        // percent of scroll (100) traveled to full opacity
        const dist = 100;
        const travel = position - 250; // 250 = full opacity start point
        const ratio = (travel / dist) * 100;
        track = 200 + ratio;
      } else {
        track = 300;
      }

      // return converts to proper ratio
      return {
        transform: `translateY( calc( ${track / 3}vh - 100% ) )`,
      };
    },
  },
  mounted: function() {
    this.container = document.querySelector('[data-module="about"]');
  },
  methods: {
    updateSectionScrollState: function(state) {
      this.sectionScroll = state;
    },
  },
};
</script>

<style lang="scss">
@import '~@/styles/_variables.scss';
@import '~@/styles/_mixins.scss';
@import '~@/styles/_typography.scss';

[data-module="about"] {
  @include spacer();

  .content__anchor {
    position: absolute;
    top: 25vh;
  }

  [data-anchor="topOfAboutContent"] {
    position: absolute;
    top: 0;
  }

  [data-anchor="bottomOfAboutContent"] {
    position: absolute;
    bottom: 0;
  }

  .about__content {
    margin-right: 4rem;

    &[data-visibility="mobile"] {
      margin: 0;
    }
  }

  .about__nav {
    display: flex;
    flex-direction: column;

    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    background: $color--quaternary-midtone;
    height: 100vh;
    width: 4rem;

    &.following {
      position: fixed;
    }

    &.fixed--bottom {
      position: absolute;
      top: auto;
      bottom: 0;
      right: 0;
    }
  }

  .nav__indicator {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 33.33%;
    background: white;
    transform: translateY(-100%);
  }

  .nav__section-link {
    flex: 1;

    // transition: all 250ms;

    // &.active {
    //   color: $color--primary-midtone;
    // }

    // &:not(.active) {
    //   background: $color--quaternary-midtone;
    // }
  }

  .section-link__copy {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-90deg);
    white-space: nowrap;
    color: $color--primary-dark;
  }

  .tab-number {
    position: absolute;
    top: 0;
    transform: translateX(-100%);
  }

  .headline {
    margin-top: 2rem;

    [data-ornament] {
      display: block;
      width: rem(80px);
      margin-bottom: rem(27px);
    }
  }

  .services {
    margin: 0;
    list-style: none;
    padding: 0;

    display: flex;
    flex-wrap: wrap;
  }

  .service {
    width: 50%;
    @include sansserif( $size: (13, 20, 0) );
    padding: 0 0.5em 1em 0;

    @include mq($bp--mobile, down) {
      width: 100%;
      padding-right: 0;
    }
  }

  // laying out all the sections
  .graphic {
    [class*="quadrant"] {
      & > img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
      }
    }
  }

  .about-subsection[data-visibility="mobile"] {
    .graphic {
      @include spacer();
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr;
    }

    .content {
      @include spacer();
      @include grid($subgrid: true, $columns: 1);
    }
  }

  .about-subsection:not([data-visibility="mobile"]) {
    display: grid;
    grid-template-rows: auto;
    grid-gap: 0;

    .content {
      grid-column: 2 / span 1;
      grid-row: 1 / span 1;
      align-self: flex-start;
      height: 100vh;
      display: flex;
      align-items: center;

      &__container {
        padding: 0 3rem;
      }
    }

    .graphic {
      grid-column: 1 / span 1;
      grid-row: 1 / span 1;
      height: 100vh;

      display: grid;
      grid-template-rows: repeat(5, 1fr);
      grid-template-columns: repeat(2, 1fr);

      &.following {
        position: fixed;
        top: 0;
      }

      &.fixed--bottom {
        position: absolute;
        bottom: 0;
        left: 0;
      }
    }

    &[data-visibility="compact"] {
      grid-template-columns: 40vh calc(100% - 40vh);

      .content {
        &.fixed {
          position: fixed;
          width: 100%;
          max-width: $layout--max-content-container-width;
          margin: 0 auto;
          top: 0;
          padding-left: 40vh;
          padding-right: 4rem;
        }

        &.fixed--bottom {
          align-self: flex-end;
        }
      }

      .graphic {
        width: 40vh;
      }
    }
    
    &[data-visibility="full"] {
      grid-template-columns: 80vh calc(100% - 80vh);

      .content {
        &.fixed {
          position: fixed;
          width: 100%;
          max-width: $layout--max-content-container-width;
          margin: 0 auto;
          top: 0;
          padding-left: 80vh;
          padding-right: 4rem;
        }

        &.fixed--bottom {
          align-self: flex-end;
        }
      }

      .graphic {
        width: 80vh;
      }
    }
  }
}
</style>




// WEBPACK FOOTER //
// src/modules/hcAbout.vue